import {
  AfterContentInit,
  Component,
  ElementRef,
  HostListener,
  Inject,
  OnDestroy,
  Optional,
  Renderer2,
  ViewChild,
  ViewContainerRef
} from "@angular/core";
import {PopoverCompileContext} from "../../PopoverCompileContext";
import {IPopoverOptions} from "../../IPopoverOptions";
import {PopoverOptions} from "../../PopoverOptions";
import {POPOVER_COMPONENT_SELECTOR} from "../../PopoverInstance";
import {PopoverConfig} from "../../PopoverConfig";
import {applyArrowStyles, calculatePosition, IArrowPosition, IBox, IRect} from "../../../position";
import {IPopoverHostOverride, POPOVER_HOST_OVERRIDE} from "../../host_override";

@Component({
  selector: POPOVER_COMPONENT_SELECTOR,
  styleUrls: [
    "./n-popover-window.css"
  ],

  templateUrl: "./n-popover-window.html",
})

export class PopoverWindowComponent implements AfterContentInit, OnDestroy {
  @ViewChild("popover", {static: true})
  popoverEl: ElementRef;

  @ViewChild("popoverContent", {read: ViewContainerRef, static: true})
  contentVc: ViewContainerRef;

  @ViewChild("arrow", {read: ElementRef, static: true})
  arrow: ElementRef;

  hostOverrideEl: HTMLElement;

  private options: IPopoverOptions;
  private mobileViewActive: boolean;
  private origHostOverflow: string = null;

  constructor(options: PopoverOptions,
              private config: PopoverConfig,
              private compileCtx: PopoverCompileContext,
              private el: ElementRef,
              private renderer: Renderer2,
              @Optional() @Inject(POPOVER_HOST_OVERRIDE) hostOverride: IPopoverHostOverride) {
    this.hostOverrideEl = hostOverride ? (hostOverride.getHost() as HTMLElement) : null;
    this.options = options.options;
  }

  ngAfterContentInit(): any {

    this.renderer.addClass(this.popoverEl.nativeElement, "__" + this.options.position || "bottom");

    if (this.options.animate) {
      this.renderer.addClass(this.popoverEl.nativeElement, "__animate");
    }

    if (this.options.popoverClass) {
      this.renderer.addClass(this.popoverEl.nativeElement, this.options.popoverClass);
    }

    /*add backdrop to host class*/
    if (this.options.mobileView) {
      this.renderer.addClass(this.el.nativeElement, "__backdrop");
    }

    this.compileCtx.compile(this.contentVc);

    this.mobileViewActive = this.getIsMobileView();

    this.positionPopover(true);

    Promise.resolve().then(() => {
      this.renderer.addClass(this.el.nativeElement, "__show");
      if (this.options.wasShown) {
        this.options.wasShown();
      }
    });
  }

  hide() {
    this.renderer.removeClass(this.el.nativeElement, "__show");
    this.renderer.addClass(this.el.nativeElement, "__hide");
    this.cleanupHostStyleOverride();
  }

  cleanupHostStyleOverride() {
    if (this.hostOverrideEl && this.origHostOverflow !== null) {
      this.renderer.setStyle(this.hostOverrideEl, "overflow", this.origHostOverflow);
    }
  }

  positionPopover(hide?: boolean) {
    const element = this.popoverEl.nativeElement;
    // открыли первый раз - нужно показать её, чтобы можно было высчитать размер, но показать слева за пределами экрана
    if (hide) {
      this.renderer.setStyle(element, "left", "-9999px");
      this.renderer.setStyle(element, "position", this.options.fixed ? "fixed" : "absolute");
    }

    if (this.mobileViewActive) {
      this.renderer.addClass(this.el.nativeElement, "__mobile-view");
      if (this.hostOverrideEl) {
        this.renderer.setStyle(this.el.nativeElement, "top", this.hostOverrideEl.scrollTop + "px");
        this.origHostOverflow = this.hostOverrideEl.style.overflow;
        this.renderer.setStyle(this.hostOverrideEl, "overflow", "hidden");
      }
    } else {
      this.renderer.removeClass(this.el.nativeElement, "__mobile-view");
    }

    Promise.resolve().then(() => {
      let targetRect: IRect;
      let targetElement: HTMLElement;
      if (this.options.targetElementRef) {
        targetElement = this.options.targetElementRef.nativeElement;
        if (!targetElement.parentElement) {
          // target element may be removed from the DOM
          return;
        }
        targetRect = targetElement.getBoundingClientRect();
        const offset = this.options.targetElementOffset;
        if (offset) {
          const pos = this.options.position;
          const vertical = pos === "bottom" || pos === "top";
          targetRect = {
            left: targetRect.left - (vertical ? 0 : offset),
            top: targetRect.top - (vertical ? offset : 0),
            right: targetRect.right + (vertical ? 0 : offset),
            bottom: targetRect.bottom + (vertical ? offset : 0)
          };
        }
      } else {
        targetRect = {
          right: this.options.posX,
          left: this.options.posX,
          top: this.options.posY,
          bottom: this.options.posY
        };
      }

      let arrowSize: IBox = {width: 2, height: 2};
      if (!this.options.hideArrow) {
        switch (this.options.position) {
          case "left":
          case "right":
            arrowSize = {width: 11, height: 22};
            break;
          case "top":
          case "bottom":
            arrowSize = {width: 22, height: 11};
            break;
        }
      } else {
        this.arrow.nativeElement.style.display = "none";
      }


      const hostOverride = this.hostOverrideEl;

      const parentElement = this.options.parentContainer as HTMLElement || hostOverride;

      const renderInTargetElement = this.options.renderInHost || targetElement && parentElement === targetElement;
      const container: IBox = parentElement && !renderInTargetElement ? {
        width: parentElement.clientWidth,
        height: parentElement.clientHeight
      } : {
        width: (document.body.offsetWidth || document.body.clientWidth),
        height: window.innerHeight
      };

      const position = calculatePosition(this.options.position,
        targetRect,
        {height: element.clientHeight, width: element.clientWidth},
        container, this.config.paddings, arrowSize,
        this.options.horizontalAlign
      );

      if (parentElement && !renderInTargetElement) {
        position.left -= window.pageXOffset - parentElement.scrollLeft;
        position.top -= window.pageYOffset - parentElement.scrollTop;
      } else if (renderInTargetElement) {
        position.left -= window.pageXOffset + targetRect.left;
        position.top -= window.pageYOffset + targetRect.top;
      }

      let popoverY;

      const localMobileViewActive = element.clientHeight > window.innerHeight;
      if (localMobileViewActive && !this.mobileViewActive) {
        this.renderer.addClass(this.el.nativeElement, "__mobile-view");
      }

      if (!this.mobileViewActive && !localMobileViewActive) {
        this.renderer.setStyle(element, "left", `${Math.round(position.left)}px`);
        this.renderer.setStyle(element, "right", `auto`);
        popoverY = position.top + (this.options.fixed ? 0 : (window.pageYOffset || document.documentElement.scrollTop));
        this.renderer.setStyle(element, "top", `${Math.round(popoverY)}px`);
      }

      if (this.options.setupMinWidth) {
        const minWidth = targetRect.right - targetRect.left;
        if (minWidth > 0) {
          this.renderer.setStyle(element, "minWidth", `${minWidth}px`);
        }
      }

      if (!this.options.hideArrow) {
        this.positionArrow(position.arrowPos);
      }
    });
  }

  ngOnDestroy() {
    this.cleanupHostStyleOverride();
    this.compileCtx.destroy();
  }

  @HostListener("window:resize")
  onResize() {
    this.mobileViewActive = this.getIsMobileView();
    if (!this.mobileViewActive) {
      this.cleanupHostStyleOverride();
    }
    this.positionPopover();
  }


  private positionArrow(arrowPos: IArrowPosition) {
    this.renderer.addClass(this.arrow.nativeElement, "__" + arrowPos.arrowPlacement);
    applyArrowStyles(this.renderer, this.arrow, arrowPos);

  }

  private getIsMobileView(): boolean {
    if (this.options.mobileView) {
      const wWidth = window.innerWidth
        || document.documentElement.clientWidth
        || document.body.clientWidth;
      return wWidth < 768;
    } else {
      return false;
    }
  }

}
